import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkStatus } from '@apollo/client';
import { InMemorySupportersListQuery } from '../../../types/__generated__';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import PageSection, { PageSectionProps } from '../PageSection/PageSection';
import { InMemorySupportersListSkeleton } from './InMemorySupportersListSkeleton';
import { SupporterCard } from '../SupporterCard/SupporterCard';
import styles from './InMemorySupportersList.scss';
import classnames from 'classnames';
import '@justgiving/loading/src/Loading.scss';
import DOCUMENT from './InMemorySupportersList.gql';

export const InMemorySupportersList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { data, fetchMore, networkStatus, refetch } = useFullPageQuery<
    InMemorySupportersListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 6,
    },
  });

  const pageSectionProps: PageSectionProps = {
    title: t('inMemory.supporters.title'),
    hasBorder: true,
    className: styles.container,
  };

  if (networkStatus === NetworkStatus.loading) {
    return (
      <PageSection {...pageSectionProps}>
        <InMemorySupportersListSkeleton animated displayRefreshButton={false} />
      </PageSection>
    );
  }

  if (!data?.page?.donations) {
    return (
      <PageSection {...pageSectionProps}>
        <InMemorySupportersListSkeleton onRefresh={refetch} />
      </PageSection>
    );
  }

  const {
    page: {
      donations: {
        edges,
        pageInfo: { hasNextPage, endCursor },
        totalCount,
      },
    },
  } = data;

  if (totalCount === 0) {
    return (
      <PageSection {...pageSectionProps}>
        <p className="jg-space-mbn" data-qa="no-supporters">
          {t('inMemory.supporters.noSupportersMessage')}{' '}
          <a href="#in-memory-fundraisers">
            {t('inMemory.supporters.noSupportersDonateLink')}
          </a>
        </p>
      </PageSection>
    );
  }

  async function handleFetchMore() {
    await fetchMore({
      variables: { first: 6, after: endCursor },
    });
  }

  return (
    <PageSection {...pageSectionProps}>
      <ul
        data-qa="imo-supporters-list"
        className={classnames(styles.list_wrapper, 'jg-space-ptsm')}
      >
        {totalCount > 0 &&
          edges.map(supporter => {
            const {
              avatar,
              amount,
              giftAidAmount,
              message,
              creationDate,
              displayName,
              legacyId,
            } = supporter.node;
            return (
              <SupporterCard
                key={legacyId}
                avatarImageUrl={avatar}
                displayName={displayName ?? undefined}
                creationDate={creationDate}
                message={message ?? undefined}
                amount={amount ?? undefined}
                giftAidAmount={giftAidAmount ?? undefined}
              />
            );
          })}
      </ul>

      {hasNextPage && (
        <div className={styles.button_wrapper}>
          <LoadMoreButton
            className={styles.button}
            loading={networkStatus === NetworkStatus.fetchMore}
            onClick={handleFetchMore}
            trackingData={{
              action_name: 'click',
              subtype: 'button',
              event: 'click',
              event_value: 'show more',
              sub_section: 'in memory supporters',
            }}
            trackingIdentifier="in-memory-show-more-supporters-button"
          >
            {t('inMemory.supporters.showMoreSupporters')}
          </LoadMoreButton>
        </div>
      )}
    </PageSection>
  );
};
